import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { Building2, LogOut, Settings, X } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';

import { RootState } from '../../../../redux';
import Dialog from '../../../shared/PFDialog/PFDialogContainer';
import {
  AvatarSize,
  Tenant,
  TenantProfileDrawerProps,
  UserProfile,
} from '../interface/app-header';
import useToken from '../../../../hooks/useToken';
import { checkPermission, getLoginUserId } from '../../../../utils/Helpers';
import { URL_CONSTANTS } from '../../../../constants/urlConstants';
import {
  setPermission,
  setUserTenants,
} from '../../../../redux/slices/permission.slice';
import {
  setClientId,
  setPageHeaderComponent,
  setTabs,
} from '../../../../redux/slices/page-header.slice';
import clearMasterDataHook from '../../../../redux/slices/master/clearMasterDataHook';
import { clientDetails, EVENT_AND_PATH_CONSTANT } from '../../../../constants';
import permissions from '../../../../config/permissions';
import { userAcitivityWorkerHelper } from '../../../../userActivityWorker/helper';
import { getUserProfile } from '../../../ProjectManagement/ProjectInfo/ProjectDetails.service';
import { setUserProfileData } from '../../../../redux/slices/userProfile.slice';
import { getClientDetails } from '../../../Admin/Configuration/RestrictAccessbyRole.service';

const TenantProfileDrawer: React.FC<TenantProfileDrawerProps> = ({
  handleUserSettingsVisibility,
  myPermission,
}) => {
  const clearMasterData = clearMasterDataHook();
  const userProfileRef = useRef<Menu>(null);
  const toast = useRef<Toast>(null);
  const dispatch = useDispatch();
  const history = useHistory();

  const { logout, accessToken } = useToken();
  const [visible, setVisible] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [searchTenants, setSearchTenants] = useState('');
  const [isExpandTenantList, setIsExpandTenantList] = useState(false);
  const getClientId = localStorage.getItem('client_id');
  const { tabs } = useSelector((state: RootState) => state.pageHeader);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [userProfile, setUserProfile] = React.useState<UserProfile>({});

  const userId = localStorage.getItem('user_id');

  const filteredTenant =
    Array.isArray(tenants) && (isExpandTenantList || searchTenants.length > 2)
      ? tenants?.filter(item =>
          item?.client_name
            ?.toLowerCase()
            ?.includes(searchTenants?.toLowerCase())
        )
      : [];
  const loginUserId: string | null = getLoginUserId();
  useEffect(() => {
    if (accessToken) {
      getAllUserTenants();
    }
  }, [loginUserId, localStorage.getItem('user_id')]);

  const getAllUserTenants = async () => {
    const updatedLoginUserId: string | null = getLoginUserId();
    if (updatedLoginUserId) {
      const response = await axios.get<Tenant[]>(
        `${URL_CONSTANTS.LOGIN.baseUrl}/get-all-user-tenants`
      );

      if (response?.data) {
        const tenantsData = Array.isArray(response?.data) ? response?.data : [];
        const firstPermIndex = tenantsData.findIndex(
          item => item?.hasWebPermission === true
        );
        setTenants(tenantsData);
        dispatch(setUserTenants(tenantsData));

        if (firstPermIndex >= 0) {
          let firstTenantsId = tenantsData[firstPermIndex]?.client_id;
          let firstTenantsName = tenantsData[firstPermIndex]?.client_name;

          const storedClientId = localStorage.getItem('client_id');
          const storedClientName = localStorage.getItem('client_name');

          if (storedClientId) {
            firstTenantsId = storedClientId;
          }
          if (storedClientName) {
            firstTenantsName = storedClientName;
          }
          if (!storedClientId && firstTenantsId) {
            localStorage.setItem('client_id', String(firstTenantsId));
          }
          if (!storedClientName && firstTenantsName) {
            localStorage.setItem('client_name', String(firstTenantsName));
          }

          dispatch(setClientId(firstTenantsId));
          getAllUserPermissionsForTenant(firstTenantsId);
        }
      } else {
        if (
          localStorage.getItem('is_mpin_enabled') === 'true' &&
          localStorage.getItem('pin_verify')
        ) {
          localStorage.removeItem('access_token');
          return;
        } else {
          logout();
        }
      }
    }
  };

  const getAllUserPermissionsForTenant = async (tenant_id: string) => {
    if (loginUserId) {
      try {
        const response = await axios.get(
          `${URL_CONSTANTS.LOGIN.baseUrl}/get-all-user-permissions-for-tenant?client_id=${tenant_id}&user_id=${loginUserId}`
        );
        if (response?.data) {
          dispatch(setPermission(response?.data));
          localStorage.setItem('permissions', JSON.stringify(response?.data));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };
  const switchTenant = async (client_id: string, client_name: string) => {
    clearMasterData();
    localStorage.removeItem('project_items_view');
    localStorage.removeItem('show_new_resource_list');
    localStorage.removeItem('client_details');
    localStorage.setItem('client_id', String(client_id));
    localStorage.setItem('client_name', client_name);
    dispatch(setClientId(String(client_id)));
    dispatch(
      setPageHeaderComponent({
        activeDashboard: null,
      })
    );

    await getAllUserPermissionsForTenant(client_id);

    try {
      const details = await getClientDetails();
      if (details) {
        localStorage.setItem('client_details', JSON.stringify(details));
      }
    } catch (error) {
      console.log(error);
    }

    if (!tabs?.[String(client_id)])
      dispatch(setTabs({ ...(tabs ?? {}), [String(client_id)]: [] }));

    if (history?.location?.pathname !== '/dashboard') {
      history.push('/dashboard');
    }
    window.location.reload();
  };

  const fetchUserProfile = async () => {
    if (axios.defaults.headers.common['client-id']) {
      const userData = await getUserProfile(setUserProfile);
      localStorage.setItem('loggedInUserEmail', userData?.email);
      const expiry =
        parseInt(clientDetails?.login_configuration?.refresh_token_time) *
        24 *
        60 *
        60 *
        1000;
      if (userData?.email && expiry) {
        const expiresUTC = new Date(Date.now() + expiry).toUTCString();
        document.cookie = `loggedInUserEmail=${encodeURIComponent(userData?.email)}; expires=${expiresUTC}; path=/`;
      }
      dispatch(setUserProfileData(userData));
    }
  };

  React.useEffect(() => {
    fetchUserProfile();
  }, [axios.defaults.headers.common['client-id']]);

  const userInitials = userProfile?.first_name
    ? `${userProfile?.first_name.charAt(0).toUpperCase() ?? ''}${userProfile?.last_name?.charAt(0)?.toUpperCase() ?? ''}`
    : localStorage.getItem('logged_in_user')?.charAt(0)?.toUpperCase();

  const renderProfileImage = (size: AvatarSize) => {
    return (
      <Avatar
        label={userInitials}
        shape="circle"
        size={size}
        pt={{
          root: {
            className:
              'pf-surface-primary text-primary shadow-3 border-2 border-white w-2rem h-2rem md:w-3rem md:h-3rem',
          },
          label: {
            className: 'text-sm md:text-xl',
          },
        }}
      />
    );
  };

  return (
    <>
      <Toast ref={toast} />

      <Dialog
        header="Confirm"
        visible={visible}
        className="w-11 lg:w-6 xl:w-5"
        onHide={() => setVisible(false)}
      >
        <p className="m-0">
          <i className="p-confirm-dialog-icon pi pi-exclamation-triangle text-lg"></i>{' '}
          Are you sure you want to proceed?
        </p>
        <div className="text-right mt-3">
          <Button
            outlined
            size="small"
            className="mr-2 text-sm"
            onClick={() => setVisible(false)}
            disabled={isLoggingOut}
          >
            No
          </Button>
          <Button
            onClick={() => {
              if (
                clientDetails?.user_activity_log &&
                checkPermission(permissions?.userActivityLogs?.view)
              ) {
                setIsLoggingOut(true);
                userAcitivityWorkerHelper({
                  quickSync: true,
                  customPayload: {
                    action: EVENT_AND_PATH_CONSTANT?.USER_LOGOUT?.EVENT,
                    data: {
                      user_id: userId,
                      user_name: localStorage.getItem('logged_in_user'),
                      date_time: new Date().toISOString(),
                    },
                    path: EVENT_AND_PATH_CONSTANT?.USER_LOGOUT?.PATH,
                    project_id: null,
                    api_status: true,
                  },
                  onComplete: () => {
                    logout();
                  },
                });
              } else {
                setIsLoggingOut(true);
                logout();
              }
            }}
            label={isLoggingOut ? 'Logging out...' : 'Yes'}
            size="small"
            disabled={isLoggingOut}
          />
        </div>
      </Dialog>

      <Menu
        model={[
          {
            items: [
              {
                template: () => (
                  <div className="w-12 -mt-5">
                    <div className="surface-ground flex p-3 text-sm justify-content-between border-round-top-md border-bottom-1 surface-50 border-200">
                      Account{' '}
                      <span>
                        <X
                          size="small"
                          className="w-1rem cursor-pointer"
                          onClick={e => userProfileRef.current?.toggle(e)}
                        />
                      </span>
                    </div>
                    <div className="grid grid-nogutter px-2 lg:px-3 mt-2">
                      <div className="col-auto pr-2">
                        {renderProfileImage('xlarge')}
                      </div>
                      <div className="col align-items-center flex pl-1">
                        <div className="text-sm">
                          <span className="capitalize">
                            {userProfile?.first_name ?? ''}{' '}
                            {userProfile?.last_name ?? ''}
                          </span>
                          <div className="text-10">
                            {userProfile?.email || ''}
                          </div>
                        </div>
                      </div>
                      <div className="col-12 py-2">
                        {!myPermission && (
                          <Button
                            className="text-sm p-button p-component p-button-sm w-full text-center flex justify-content-center"
                            size="small"
                            onClick={event => {
                              event.preventDefault();
                              handleUserSettingsVisibility(true);
                            }}
                          >
                            <Settings className="mr-2" size="18" />
                            Settings
                          </Button>
                        )}
                      </div>
                    </div>
                    {tenants?.length > 1 && (
                      <div className="w-full border-top-1 border-bottom-1 border-200 surface-50"></div>
                    )}
                    <div className="grid grid-nogutter px-2 lg:px-3">
                      <div className="w-full mt-3">
                        {tenants?.length > 1 && (
                          <div className="w-full">
                            <div className="flex align-items-center w-full gap-2">
                              <span className="p-input-icon-left flex-1 w-full">
                                <i className="pi pi-search" />
                                <InputText
                                  id="searchTenant"
                                  name="searchTenant"
                                  placeholder="Search tenants..."
                                  value={searchTenants}
                                  onChange={e => {
                                    setSearchTenants(e?.target?.value);
                                  }}
                                  onKeyDown={e => e.stopPropagation()}
                                  autoComplete="off"
                                  className="w-full p-inputtext-sm pl-4"
                                />
                              </span>
                              <Button
                                type="button"
                                className="p-button p-button-text p-button-sm p-0 flex align-items-center justify-content-center"
                                onClick={() =>
                                  setIsExpandTenantList(!isExpandTenantList)
                                }
                              >
                                <i
                                  className={`pi pi-chevron-${isExpandTenantList ? 'up' : 'down'} text-600 text-lg`}
                                />
                              </Button>
                            </div>
                          </div>
                        )}
                        <Tooltip
                          target=".access-permission"
                          className="text-xs p-1"
                        />
                        {isExpandTenantList || searchTenants.length > 2 ? (
                          <ul
                            className="overflow-auto mt-2"
                            style={{
                              maxHeight: 'calc(100vh - 435px)',
                              minHeight: '50px',
                            }}
                          >
                            {filteredTenant?.length === 0 ? (
                              <p
                                className="mt-2 text-center text-500 text-xs"
                                style={{ fontStyle: 'italic' }}
                              >
                                We couldn’t find any tenants.
                              </p>
                            ) : (
                              filteredTenant?.map((item, index) => {
                                return (
                                  <li
                                    className={`w-12 my-2 border-round-lg border-1 hover:surface-50 ${
                                      getClientId === item?.client_id
                                        ? 'border-1 pf-border-primary'
                                        : 'border-100'
                                    }`}
                                    key={`side-menu-${index}`}
                                  >
                                    <i
                                      className="access-permission text-xs cursor-pointer flex h-3rem"
                                      data-pr-tooltip={
                                        !item?.hasWebPermission
                                          ? "You don't have permission to access this tenant"
                                          : undefined
                                      }
                                      data-pr-position="left"
                                      data-pr-at="left top+40"
                                      data-pr-my="right"
                                    >
                                      <Button
                                        onClick={() => {
                                          localStorage.removeItem(
                                            'common_filters'
                                          );
                                          localStorage.removeItem(
                                            'loggedInViaPassword'
                                          );
                                          localStorage.removeItem(
                                            'wantSetupMpin'
                                          );
                                          switchTenant(
                                            item?.client_id,
                                            item?.client_name
                                          );
                                          if (!item?.hasWebPermission) {
                                            localStorage.setItem(
                                              'webPermission',
                                              'true'
                                            );
                                          } else {
                                            localStorage.setItem(
                                              'webPermission',
                                              'false'
                                            );
                                          }
                                        }}
                                        link
                                        className={`uppercase text-sm p-2 text-left text-color w-full border-round-lg ${getClientId === item?.client_id ? 'pf-surface-light-primary-50' : 'bg-white'}`}
                                        pt={{
                                          root: {
                                            className: `focus:shadow-none`,
                                          },
                                        }}
                                        disabled={!item?.hasWebPermission}
                                      >
                                        <span
                                          className={`h-2rem w-2rem border-round-xs ${getClientId === item?.client_id ? 'pf-surface-light-primary-200' : 'surface-200'} flex align-items-center justify-content-center border mr-2 border-round-md`}
                                        >
                                          <Building2
                                            size="18"
                                            className={`text-sm ${
                                              getClientId === item?.client_id
                                                ? 'text-color-primary'
                                                : 'text-color'
                                            }`}
                                          />
                                        </span>
                                        {item?.client_name}
                                        {getClientId === item?.client_id && (
                                          <span className="green-animated-dot relative ml-auto mb-2 mr-2"></span>
                                        )}
                                      </Button>
                                    </i>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        ) : tenants?.length > 1 ? (
                          <p
                            className="mt-2 text-center text-500 text-xs"
                            style={{ fontStyle: 'italic' }}
                          >
                            Type at least 3 characters to search.
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="pt-2 border-top-1 border-100 px-2 lg:px-3">
                      <Button
                        className="text-sm p-button p-component p-button-sm w-full text-center flex justify-content-center bg-red-600"
                        size="small"
                        onClick={() => setVisible(true)}
                        severity="danger"
                      >
                        <LogOut className="mr-2" size="18" />
                        Logout
                      </Button>
                    </div>
                  </div>
                ),
              },
            ],
          },
        ]}
        popup
        ref={userProfileRef}
        className="w-20rem md:w-24rem"
      />
      <div
        className=" flex align-items-center justify-content-end cursor-pointer ml-auto"
        onClick={e => userProfileRef?.current?.toggle(e)}
        role="button"
        tabIndex={0}
      >
        {renderProfileImage('large')}
      </div>
    </>
  );
};

export default TenantProfileDrawer;
